import { ChangeEvent, FC, FormEvent, useMemo, useRef, useState } from "react";

import {
  FoodBusinessHearAboutSource,
  getFoodBusinessSubmitErrorMessage,
} from "../api/foodBusiness";
import DesktopButton from "../components/Desktop/Button";
import MobileButton from "../components/Mobile/Button";
import { useSubmitFoodBusinessForm } from "../hooks/useSubmitFoodBusinessForm";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const FOOD_BUSINESS_HEAR_ABOUT_OPTIONS: {
  value: FoodBusinessHearAboutSource;
  label: string;
}[] = [
  { value: "word_of_mouth", label: "Word of Mouth" },
  { value: "online_search", label: "Online Search (Google, etc.)" },
  { value: "social_media", label: "Social Media (Facebook, X, Instagram, etc.)" },
  {
    value: "online_ads_print",
    label: "Online Ads & Print (Magazine, Newspaper, etc.)",
  },
  { value: "event_trade_show", label: "Event or Trade Show" },
  { value: "email_marketing", label: "Email Marketing" },
  { value: "blog_article", label: "Blog or Online Article" },
  { value: "dont_remember", label: "Don't Remember" },
];

type FoodBusinessFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  businessName: string;
  businessWebsite: string;
  businessZipCode: string;
  hearAboutSource: FoodBusinessHearAboutSource | "";
};

type FoodBusinessFormErrors = Partial<Record<keyof FoodBusinessFormValues, string>>;
type SubmissionState = "idle" | "success" | "error";

const emptyValues: FoodBusinessFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  businessName: "",
  businessWebsite: "",
  businessZipCode: "",
  hearAboutSource: "",
};

/** Validates the food business community signup fields before backend submission. */
const validateFoodBusinessForm = (
  values: FoodBusinessFormValues
): FoodBusinessFormErrors => {
  const errors: FoodBusinessFormErrors = {};

  if (!values.firstName.trim()) errors.firstName = "First name is required.";
  if (!values.lastName.trim()) errors.lastName = "Last name is required.";
  if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (!values.businessName.trim()) {
    errors.businessName = "Business name is required.";
  }
  if (!values.businessZipCode.trim()) {
    errors.businessZipCode = "Business zip code is required.";
  }
  if (!values.hearAboutSource) {
    errors.hearAboutSource = "Please tell us how you heard about us.";
  }

  return errors;
};

type FoodBusinessFormProps = {
  submitLabel?: string;
  /** Mobile PageForm uses tighter field spacing (matches legacy mobile form). */
  layout?: "desktop" | "mobile";
};

/**
 * Native food business community signup form.
 * Submits to dashboard-backend (replaces HubSpot embed on Home, Food Entrepreneurs, Resource Providers).
 */
const FoodBusinessForm: FC<FoodBusinessFormProps> = ({
  submitLabel = "Join",
  layout = "desktop",
}) => {
  const [values, setValues] = useState<FoodBusinessFormValues>(emptyValues);
  const [errors, setErrors] = useState<FoodBusinessFormErrors>({});
  const [state, setState] = useState<SubmissionState>("idle");
  const [submitError, setSubmitError] = useState("");
  const statusRef = useRef<HTMLDivElement>(null);
  const { submitFoodBusiness, isSubmittingFoodBusiness } = useSubmitFoodBusinessForm();

  const fieldIds = useMemo(
    () => ({
      firstName: "food-business-first-name",
      lastName: "food-business-last-name",
      email: "food-business-email",
      businessName: "food-business-business-name",
      businessWebsite: "food-business-business-website",
      businessZipCode: "food-business-business-zip",
      hearAboutSource: "food-business-hear-about",
    }),
    []
  );

  const fieldSpacing = layout === "mobile" ? "mb-[16px]" : "mb-[24px]";
  const inputClassName = `w-full ${fieldSpacing} border-none bg-text-1000 px-[15px] h-[51px] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primaryDark-0`;
  const rootClassName =
    layout === "mobile"
      ? "food-business-form-root food-business-form-root--native food-business-form-root--mobile"
      : "food-business-form-root food-business-form-root--native";

  const handleChange =
    (field: keyof FoodBusinessFormValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const nextValue = event.target.value;
      setValues((current) => ({ ...current, [field]: nextValue }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: undefined }));
      }
      if (submitError) setSubmitError("");
    };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateFoodBusinessForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitError("");

    try {
      await submitFoodBusiness({
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        email: values.email.trim(),
        business_name: values.businessName.trim(),
        business_website: values.businessWebsite.trim(),
        business_zip_code: values.businessZipCode.trim(),
        hear_about_source: values.hearAboutSource as FoodBusinessHearAboutSource,
        form_source: "food_business_form",
      });
      setState("success");
      statusRef.current?.focus();
    } catch (error) {
      setState("error");
      setSubmitError(getFoodBusinessSubmitErrorMessage(error));
      statusRef.current?.focus();
    }
  };

  if (state === "success") {
    return (
      <div className={rootClassName}>
        <div
          ref={statusRef}
          tabIndex={-1}
          className="rounded-[8px] bg-white px-[20px] py-[24px] text-center"
          role="status"
        >
          <p className="mb-[8px] text-[18px] font-raleway font-bold text-primaryDark-0">
            Welcome to the HomeCook community!
          </p>
          <p className="text-[14px] font-open-sans text-primaryDark-400">
            Check your inbox for a welcome email with next steps.
          </p>
        </div>
      </div>
    );
  }

  const SubmitButton = layout === "mobile" ? MobileButton : DesktopButton;

  return (
    <div className={rootClassName}>
      <form className="w-full" noValidate onSubmit={handleSubmit}>
        <div>
          <label htmlFor={fieldIds.firstName} className="sr-only">
            First Name
          </label>
          <input
            id={fieldIds.firstName}
            name="firstName"
            type="text"
            autoComplete="given-name"
            placeholder="First Name"
            className={inputClassName}
            value={values.firstName}
            onChange={handleChange("firstName")}
            aria-invalid={Boolean(errors.firstName)}
            aria-describedby={errors.firstName ? `${fieldIds.firstName}-error` : undefined}
          />
          {errors.firstName ? (
            <p id={`${fieldIds.firstName}-error`} className="mb-[12px] text-[13px] text-red-600">
              {errors.firstName}
            </p>
          ) : null}
        </div>

        <div>
          <label htmlFor={fieldIds.lastName} className="sr-only">
            Last Name
          </label>
          <input
            id={fieldIds.lastName}
            name="lastName"
            type="text"
            autoComplete="family-name"
            placeholder="Last Name"
            className={inputClassName}
            value={values.lastName}
            onChange={handleChange("lastName")}
            aria-invalid={Boolean(errors.lastName)}
            aria-describedby={errors.lastName ? `${fieldIds.lastName}-error` : undefined}
          />
          {errors.lastName ? (
            <p id={`${fieldIds.lastName}-error`} className="mb-[12px] text-[13px] text-red-600">
              {errors.lastName}
            </p>
          ) : null}
        </div>

        <div>
          <label htmlFor={fieldIds.email} className="sr-only">
            Email
          </label>
          <input
            id={fieldIds.email}
            name="email"
            type="email"
            autoComplete="email"
            placeholder="Email"
            className={inputClassName}
            value={values.email}
            onChange={handleChange("email")}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? `${fieldIds.email}-error` : undefined}
          />
          {errors.email ? (
            <p id={`${fieldIds.email}-error`} className="mb-[12px] text-[13px] text-red-600">
              {errors.email}
            </p>
          ) : null}
        </div>

        <div>
          <label htmlFor={fieldIds.businessName} className="sr-only">
            Business Name
          </label>
          <input
            id={fieldIds.businessName}
            name="businessName"
            type="text"
            autoComplete="organization"
            placeholder="Business Name"
            className={inputClassName}
            value={values.businessName}
            onChange={handleChange("businessName")}
            aria-invalid={Boolean(errors.businessName)}
            aria-describedby={
              errors.businessName ? `${fieldIds.businessName}-error` : undefined
            }
          />
          {errors.businessName ? (
            <p
              id={`${fieldIds.businessName}-error`}
              className="mb-[12px] text-[13px] text-red-600"
            >
              {errors.businessName}
            </p>
          ) : null}
        </div>

        <div>
          <label htmlFor={fieldIds.businessWebsite} className="sr-only">
            Business Website
          </label>
          <input
            id={fieldIds.businessWebsite}
            name="businessWebsite"
            type="url"
            autoComplete="url"
            placeholder="Business Website"
            className={inputClassName}
            value={values.businessWebsite}
            onChange={handleChange("businessWebsite")}
          />
        </div>

        <div>
          <label htmlFor={fieldIds.businessZipCode} className="sr-only">
            Business Zip Code
          </label>
          <input
            id={fieldIds.businessZipCode}
            name="businessZipCode"
            type="text"
            autoComplete="postal-code"
            placeholder="Business Zip Code"
            className={inputClassName}
            value={values.businessZipCode}
            onChange={handleChange("businessZipCode")}
            aria-invalid={Boolean(errors.businessZipCode)}
            aria-describedby={
              errors.businessZipCode ? `${fieldIds.businessZipCode}-error` : undefined
            }
          />
          {errors.businessZipCode ? (
            <p
              id={`${fieldIds.businessZipCode}-error`}
              className="mb-[12px] text-[13px] text-red-600"
            >
              {errors.businessZipCode}
            </p>
          ) : null}
        </div>

        <div>
          <label htmlFor={fieldIds.hearAboutSource} className="sr-only">
            How did you hear about us?
          </label>
          <select
            id={fieldIds.hearAboutSource}
            name="hearAboutSource"
            className={`${inputClassName} cursor-pointer`}
            value={values.hearAboutSource}
            onChange={handleChange("hearAboutSource")}
            aria-invalid={Boolean(errors.hearAboutSource)}
            aria-describedby={
              errors.hearAboutSource ? `${fieldIds.hearAboutSource}-error` : undefined
            }
          >
            <option value="" disabled hidden>
              How did you hear about us?
            </option>
            {FOOD_BUSINESS_HEAR_ABOUT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.hearAboutSource ? (
            <p
              id={`${fieldIds.hearAboutSource}-error`}
              className="mb-[12px] text-[13px] text-red-600"
            >
              {errors.hearAboutSource}
            </p>
          ) : null}
        </div>

        {submitError ? (
          <p
            ref={statusRef}
            tabIndex={-1}
            className="mb-[16px] text-[13px] text-red-600"
            role="alert"
          >
            {submitError}
          </p>
        ) : null}

        <SubmitButton
          label={isSubmittingFoodBusiness ? "Submitting..." : submitLabel}
          variant="secondary"
          type="submit"
          disabled={isSubmittingFoodBusiness}
          {...(layout === "desktop" ? { formButton: true } : {})}
        />
      </form>
    </div>
  );
};

export { FoodBusinessForm };
